import Layout, { Content } from 'antd/lib/layout/layout';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { compareVersions, isReleaseSkipped } from '../helpers';
import type { RootState } from '../reducers/reducers';
import type { AppComponentProps, AppRouteComponentMatchParams, Nullable } from '../typescriptHelpers';
import { antGrey, hexStringToRGBAString, runwayColors, titleStyle } from './styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import Button from 'antd/lib/button/button';
import { Event } from '../utils/analytics';
import { FetchableEntityTypeV2 } from '../entityType';
import { MehOutlined } from '@ant-design/icons';
import Result from 'antd/lib/result';
import { LiveReleaseComponent } from './appHome/LiveReleaseComponent';
import { NextReleaseComponent } from './appHome/NextReleaseComponent';
import { UpcomingReleaseComponent } from './appHome/UpcomingReleaseComponent';
import { PastReleaseComponent } from './appHome/PastReleaseComponent';
import Spin from 'antd/lib/spin';
import { trackAppRouteEvent, trackUserEvent } from '../actions/trackEvent';
import {
    selectApp,
    selectAppPlatform,
    selectAppStoreIntegration,
    selectAppStoreType,
    selectIsAppsDemoModeEnabled,
    selectWorkflowEnabledSteps,
    selectStabilityIntegration,
    selectIsWorkflowsMigratedAndEnabled,
    selectDestinationInfoByDestinationStepConfigId,
    selectWorkflowOverview,
    selectIsWorkflowOverviewPreviouslyFetchedForApp,
    selectShouldHideNonBlockingSteps,
    selectReleaseStepsBlockingConfigForReleaseType,
    selectDemoApp,
} from '../selectors/selectors';
import * as Storage from '../helpers/localStorageHelper';
import { QuickStartCIProgressBanner } from './ciQuickstart/QuickstartCIProgressBanner';
import { useFirstIncompleteQuickStartCIStep } from './ciQuickstart/hooks/useQuickStartCISteps';
import { MobileSideBarHeader } from './buildDistribution/mobileComponents/MobileSideBarHeader';
import { SkippedReleaseComponent } from './appHome/SkippedReleaseComponent';
import { QualitativeOnboardingTaskList } from '@/components/QualitativeOnboardingTaskList';
import { useGetEntityV2 } from '@/utils/hooks/helpers';
import { globalMessage } from '@/utils/globalMessage';
import { WorkflowRunOverviewCard } from './workflowOverview/WorkflowRunOverviewCard';
import Divider from 'antd/lib/divider';
import {
    AllLifecycleSteps,
    LifecycleStep,
    ReleaseStatus,
    ReleaseStepType,
    ReleaseTimelinePhase,
    ReleaseType,
} from '@/types';
import { useParams, useLocation } from 'react-router-dom';
import { DestinationSelector } from './appHome/DestinationSelector';
import { DestinationCounter } from './appHome/DestinationCounter';
import { ReleaseDescriptionWidget } from './appHome/widgets/ReleaseDescriptionWidget';
import { FeatureCompletionWidget } from './appHome/widgets/FeatureCompletionWidget';
import { ReleasePilotWidget } from './appHome/widgets/ReleasePilotWidget';
import { ReleaseTimelineWidget } from './appHome/widgets/ReleaseTimelineWidget';
import { WorkflowRunDestinationBuildsWidget } from './workflowOverview/widgets/WorkflowRunDestinationBuildsWidget';
import { WorkflowRunDatesWidgetProps } from './workflowOverview/widgets/WorkflowRunDatesWidget';
import { WorkflowRunStepsGraphWidget } from './workflowOverview/widgets/WorkflowRunStepsGraphWidget';
import { colorForWorkflowRunOverviewDisplayStatus, displayStringForDestination } from '@/helpers/displayHelpers';
import { ReleaseMetricsWidget } from './appHome/widgets/ReleaseMetricsWidget';
import { WorkflowRunOverviewDisplayStatus } from '@/types/workflowOverview';
import { WorkflowRunStatusWidgetsContainer } from './workflowOverview/WorkflowRunStatusWidgetsContainer';
import { WorkflowRunRolloutWidgetsContainer } from './workflowOverview/widgets/WorkflowRunRolloutWidgetsContainer';
import { ROUTER_REDIRECT } from '@/helpers/routeHelpers';
import { useGetAppHomeQuery } from '@/middlewares/defaultApi';

const decodeSelectedDestinationIdsFromURL = (currentSearch: string, shouldUseLocalStorage: boolean): string[] => {
    const searchParams = new URLSearchParams(currentSearch);
    const destinationIds =
        shouldUseLocalStorage ? Storage.getSelectedDestinationIdsStoredParams() : searchParams.get('destinationIds');

    if (!destinationIds) {
        return [];
    }

    try {
        return JSON.parse(destinationIds);
    } catch {
        return [];
    }
};

const encodeSelectedDestinationIdsInURL = (destinationIds: string[], currentSearch: string) => {
    const searchParams = new URLSearchParams(currentSearch);

    if (destinationIds && destinationIds.length > 0) {
        searchParams.set('destinationIds', JSON.stringify(destinationIds));
    } else {
        searchParams.delete('destinationIds');
    }

    return searchParams.toString();
};

interface LocalLocationState {
    [ROUTER_REDIRECT]: boolean;
}

export const AppHomeComponent: React.FC<AppComponentProps> = (props: AppComponentProps) => {
    const { orgId, appId } = props;
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();
    const location = useLocation<LocalLocationState>();

    const workflowId = useSelector((state: RootState) => {
        const app = selectApp(state, appId);
        return app.workflowId;
    });

    const dispatch = useDispatch();

    const [quickStartCIDismissed, setQuickStartCIDismissed] = React.useState(
        Storage.getQuickStartCIDismissedForAppId(props.appId),
    );

    const isWorkflowsMigratedAndEnabled = useSelector((state: RootState) =>
        selectIsWorkflowsMigratedAndEnabled(state, appId),
    );

    const workflowEnabledSteps = useSelector((state: RootState) => selectWorkflowEnabledSteps(state, appId));

    // for now, we will require the SRR steps be migrated / enabled in order to render the
    // workflows overview; we'll default to rendering legacy app home if they aren't.
    const isSRREnabled =
        !!workflowEnabledSteps[ReleaseStepType.Submission] &&
        !!workflowEnabledSteps[ReleaseStepType.StoreReview] &&
        !!workflowEnabledSteps[ReleaseStepType.Takeoff];

    const shouldRenderWorkflowsOverview = isWorkflowsMigratedAndEnabled && isSRREnabled;

    const isWorkflowOverviewPreviouslyFetched = useSelector((state: RootState) =>
        selectIsWorkflowOverviewPreviouslyFetchedForApp(state, appId),
    );

    const isViewingAppsInDemoMode = useSelector((state: RootState) => selectIsAppsDemoModeEnabled(state));

    const stabilityIntegration = useSelector((state: RootState) => selectStabilityIntegration(state, appId));
    const appStoreIntegration = useSelector((state: RootState) => selectAppStoreIntegration(state, appId));

    const { data, isFetching, error, refetch } = useGetAppHomeQuery(
        { orgId, appId },
        { skip: shouldRenderWorkflowsOverview || isViewingAppsInDemoMode },
    );

    const demoApp = useSelector((state: RootState) => selectDemoApp(state, appId));
    const appHome = isViewingAppsInDemoMode ? demoApp?.appHome : data?.data;

    function sortReleasesByVersion<T>(releases: Nullable<T & { version: string }>[]): T[] {
        // We need slice() since we cannot sort a frozen array in Javascript, so we're making a copy first
        return (
            releases?.slice()?.sort((releaseA, releaseB) => compareVersions(releaseA.version, releaseB.version)) ?? []
        );
    }

    const liveRelease = appHome?.liveRelease;
    const nextRelease = appHome?.nextRelease;
    const upcomingReleases = sortReleasesByVersion(appHome?.upcomingReleases);
    const pastReleases = sortReleasesByVersion(appHome?.pastReleases);

    const noReleases =
        !isFetching &&
        !liveRelease &&
        !nextRelease &&
        (!upcomingReleases || upcomingReleases?.length === 0) &&
        (!pastReleases || pastReleases?.length === 0);

    // track amplitude
    React.useEffect(() => {
        dispatch(trackAppRouteEvent({ orgId: props.orgId, appId: props.appId, eventName: Event.ViewAppHome }));
    }, [orgId, appId]);

    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));

    const saveDismissedQuickstartCI = (isDismissed: boolean) => {
        // record onboarding dismissed in local storage
        setQuickStartCIDismissed(isDismissed);
        Storage.setQuickStartCIDismissedForAppId(props.appId, isDismissed);

        dispatch(
            trackUserEvent({
                orgId: props.orgId,
                appId: props.appId,
                eventName: Event.ClickDismissQuickStartCI,
            }),
        );
    };

    const [firstIncompleteQuickStartCIStepIndex, quickStartCIStepCount] = useFirstIncompleteQuickStartCIStep(
        appId,
        orgId,
    );
    const quickStartCIProgress = ((firstIncompleteQuickStartCIStepIndex + 1) / quickStartCIStepCount) * 100;
    const showQuickStartCI =
        quickStartCIProgress < 100 && firstIncompleteQuickStartCIStepIndex !== 0 && !quickStartCIDismissed;

    const shouldHideNonBlockingSteps = useSelector((state: RootState) =>
        selectShouldHideNonBlockingSteps(state, appId),
    );
    const nonBlockingStepsConfigMap = useSelector((state: RootState) =>
        selectReleaseStepsBlockingConfigForReleaseType(state, appId),
    );

    const workflowEnabledLifecycleSteps = AllLifecycleSteps.filter((lifecycleStep) => {
        switch (lifecycleStep) {
            case LifecycleStep.Kickoff:
                return !!workflowEnabledSteps[ReleaseStepType.Kickoff];
            case LifecycleStep.Submission:
                return !!workflowEnabledSteps[ReleaseStepType.Submission];
            case LifecycleStep.Release:
                return !!workflowEnabledSteps[ReleaseStepType.Takeoff];
            default:
                return false;
        }
    });

    const destinations = useSelector((state: RootState) =>
        Object.values(selectDestinationInfoByDestinationStepConfigId(state, appId)),
    );

    const isRouterLink = !!location?.state?.[ROUTER_REDIRECT];

    const getInitialDestinationIds = () => {
        let destinationIdsFromURL = decodeSelectedDestinationIdsFromURL(window.location.search, isRouterLink);
        if (destinationIdsFromURL.length === 0) {
            destinationIdsFromURL = destinations.map((destination) => destination.stepConfigId);
        }

        encodeSelectedDestinationIdsInURL(destinationIdsFromURL, window.location.search);
        return destinationIdsFromURL;
    };

    const [selectedDestinationIds, setSelectedDestinationIds] = React.useState<string[]>(getInitialDestinationIds());

    React.useEffect(() => {
        if (location) {
            const destinationIdsFromURL = decodeSelectedDestinationIdsFromURL(window.location.search, isRouterLink);

            window.history.replaceState({}, '', window.location.pathname);

            if (destinationIdsFromURL.length > 0) {
                setSelectedDestinationIds(destinationIdsFromURL);
            }
        }
    }, [location, isRouterLink]);

    React.useEffect(() => {
        const url = new URL(window.location.toString());
        url.search = encodeSelectedDestinationIdsInURL(selectedDestinationIds, url.search);
        window.history.pushState({}, '', url);

        Storage.setSelectedDestinationIdsStoredParams(JSON.stringify(selectedDestinationIds));
    }, [selectedDestinationIds]);

    const [getWorkflowOverview, getWorkflowOverviewIsLoading, getWorkflowOverviewError] = useGetEntityV2(
        FetchableEntityTypeV2.WorkflowOverview,
        workflowId,
    );

    const [shouldDisplayOverviewFetchFailed, setShouldDisplayOverviewFetchFailed] = React.useState(false);
    React.useEffect(() => {
        if (getWorkflowOverviewError) {
            globalMessage.error(getWorkflowOverviewError);
            setShouldDisplayOverviewFetchFailed(true);
        }
    }, [getWorkflowOverviewError]);

    const isWorkflowsEnabledAndMultiDestination = isWorkflowsMigratedAndEnabled && destinations.length > 1;
    const shouldShowDestinationSelector =
        shouldRenderWorkflowsOverview && isWorkflowsEnabledAndMultiDestination && isWorkflowOverviewPreviouslyFetched;

    const workflowOverview = useSelector((state: RootState) => selectWorkflowOverview(state, appId));

    // TODO: Use RTK similarly to legacy
    React.useEffect(() => {
        if (workflowId && shouldRenderWorkflowsOverview) {
            setShouldDisplayOverviewFetchFailed(false);
            getWorkflowOverview({
                orgId,
                workflowId,
                destinationStepIds: selectedDestinationIds,
            });
        }
    }, [workflowId, selectedDestinationIds, shouldRenderWorkflowsOverview]);

    const runCardDestinationDisplayText = React.useMemo(() => {
        if (!shouldRenderWorkflowsOverview) {
            return null;
        }

        if (selectedDestinationIds.length === 1) {
            const selectedDestination = destinations.find((dest) => dest.stepConfigId === selectedDestinationIds[0]);
            return displayStringForDestination(
                selectedDestination?.platform,
                selectedDestination?.appStoreType,
                selectedDestination?.name,
            );
        } else {
            return selectedDestinationIds.length === destinations.length ? 'All destinations' : 'Selected destinations';
        }
    }, [selectedDestinationIds]);

    return (
        <MobileSideBarHeader orgId={orgId} appId={appId} title="App Home">
            <Layout
                style={{
                    overflow: 'scroll',
                    padding: 36,
                    minWidth: 980,
                }}
            >
                {/*
            Leaving this here until we can better support app-level demo mode
            <Alert
                type="info"
                icon={null}
                message="Not ready to start onboarding?"
                description={
                    <Flex vertical gap={4}>
                        <Text>
                            Try Runway in demo mode to see how the dashboard will look once setup is complete.
                        </Text>
                        <Text type="secondary">
                            Demo mode is read-only — all actions on the dashboard will be disabled.
                        </Text>
                    </Flex>
                }
                action={<TrackedButton onClick={viewInDemoMode}>🔮 Try it in demo mode 🔮</TrackedButton>}
                style={{
                    borderStyle: 'solid',
                    borderColor: hexStringToRGBAString(runwayColors.primary, 0.8),
                    borderWidth: 1,
                    marginBottom: 28,
                }}
            ></Alert> */}

                {showQuickStartCI && (
                    <QuickStartCIProgressBanner
                        appId={appId}
                        orgId={orgId}
                        onDismiss={() => saveDismissedQuickstartCI(true)}
                    />
                )}
                <QualitativeOnboardingTaskList orgId={props.orgId} appId={props.appId} />
                <Content
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {shouldShowDestinationSelector ?
                        <div style={{ marginBottom: 24, maxWidth: 480 }}>
                            <DestinationSelector
                                destinations={destinations}
                                selectedDestinationIds={selectedDestinationIds}
                                onSelectionChange={setSelectedDestinationIds}
                                requiresConfirmation
                                isLoading={getWorkflowOverviewIsLoading}
                            />
                        </div>
                    :   <Text style={{ ...titleStyle, marginBottom: 24 }}>Overview</Text>}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            paddingBottom: 65,
                            flexGrow: 1,
                            justifyContent: 'center',
                        }}
                    >
                        {shouldRenderWorkflowsOverview ?
                            <>
                                {!!workflowOverview &&
                                    !!workflowOverview.workflowRuns &&
                                    getWorkflowOverviewIsLoading === false &&
                                    shouldDisplayOverviewFetchFailed === false && (
                                        <>
                                            {workflowOverview.workflowRuns.map((singleRunOverview) => {
                                                const isUpcomingRun =
                                                    singleRunOverview.timelinePhase === ReleaseTimelinePhase.Upcoming;
                                                const isCurrentRun =
                                                    singleRunOverview.timelinePhase === ReleaseTimelinePhase.Current;
                                                const isPastRun =
                                                    singleRunOverview.timelinePhase === ReleaseTimelinePhase.Completed;
                                                const isSkippedRun = singleRunOverview.status === ReleaseStatus.Skipped;

                                                const haveAnyEvents =
                                                    !!singleRunOverview.events && singleRunOverview.events.length > 0;
                                                const haveAnyStats =
                                                    !!singleRunOverview.stats && singleRunOverview.stats.length > 0;

                                                const shouldShowTimelineWidget =
                                                    (isCurrentRun || isUpcomingRun) && haveAnyEvents;
                                                const shouldShowStatsWidget =
                                                    (isPastRun || isCurrentRun) && !isSkippedRun && haveAnyStats;

                                                const hasRollingOutDisplayStatus =
                                                    singleRunOverview.overviewDisplayStatus ===
                                                    WorkflowRunOverviewDisplayStatus.RollingOut;
                                                const hasCompletedDisplayStatus =
                                                    singleRunOverview.overviewDisplayStatus ===
                                                    WorkflowRunOverviewDisplayStatus.Completed;

                                                const shouldShowRolloutsWidgets =
                                                    hasRollingOutDisplayStatus || hasCompletedDisplayStatus;

                                                return (
                                                    <WorkflowRunOverviewCard
                                                        key={singleRunOverview.workflowRunId}
                                                        orgId={orgId}
                                                        workflowId={singleRunOverview.workflowId}
                                                        workflowRunId={singleRunOverview.workflowRunId}
                                                        releaseId={singleRunOverview.releaseId}
                                                        displayableVersion={singleRunOverview.displayableVersion}
                                                        headerComponent={
                                                            <WorkflowRunStatusWidgetsContainer
                                                                overviewDisplayStatus={
                                                                    singleRunOverview.overviewDisplayStatus
                                                                }
                                                                allDestinations={destinations}
                                                                selectedDestinationStepIds={selectedDestinationIds}
                                                                statusesByDestinationStepId={
                                                                    singleRunOverview.destinationStatusByDestinationStepId
                                                                }
                                                                phasedInfoByDestinationStepId={
                                                                    singleRunOverview.phasedReleaseInfosByDestinationStepId
                                                                }
                                                                reviewStartedDateByDestinationStepId={
                                                                    singleRunOverview.startedReviewAtByDestinationStepId
                                                                }
                                                                legacyReviewStartedDate={
                                                                    singleRunOverview.startedReviewAt
                                                                }
                                                                isHotfix={singleRunOverview.type === ReleaseType.Hotfix}
                                                            />
                                                        }
                                                        contentTopline={
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    flexDirection: 'row',
                                                                    justifyContent: 'flex-start',
                                                                    alignItems: 'center',
                                                                    gap: 17,
                                                                    width: '100%',
                                                                }}
                                                            >
                                                                {isWorkflowsEnabledAndMultiDestination && (
                                                                    <>
                                                                        <DestinationCounter
                                                                            destinations={destinations.filter((dest) =>
                                                                                selectedDestinationIds.includes(
                                                                                    dest.stepConfigId,
                                                                                ),
                                                                            )}
                                                                            size={
                                                                                selectedDestinationIds.length !== 1 ?
                                                                                    20
                                                                                :   24
                                                                            }
                                                                        />
                                                                        <div
                                                                            style={{
                                                                                fontWeight: 300,
                                                                                fontSize: 16,
                                                                                color: antGrey.grey7,
                                                                            }}
                                                                        >
                                                                            {runCardDestinationDisplayText}
                                                                        </div>
                                                                    </>
                                                                )}
                                                                <WorkflowRunDestinationBuildsWidget
                                                                    allDestinations={destinations}
                                                                    selectedDestinationStepIds={selectedDestinationIds}
                                                                    appStoreBuildsByDestinationStepId={
                                                                        singleRunOverview.selectedAppStoreBuildsByDestinationStepId
                                                                    }
                                                                />
                                                            </div>
                                                        }
                                                        contentComponent={
                                                            <>
                                                                <ReleaseDescriptionWidget
                                                                    description={
                                                                        singleRunOverview.workflowPlanningSummary
                                                                    }
                                                                />
                                                                {shouldShowStatsWidget && (
                                                                    <ReleaseMetricsWidget
                                                                        orgId={orgIdParam}
                                                                        appId={appIdParam}
                                                                        releaseId={singleRunOverview.releaseId}
                                                                        stats={singleRunOverview.stats}
                                                                    />
                                                                )}
                                                                {shouldShowTimelineWidget && (
                                                                    <ReleaseTimelineWidget
                                                                        appId={appIdParam}
                                                                        orgId={orgIdParam}
                                                                        releaseId={singleRunOverview.releaseId}
                                                                        events={singleRunOverview.events}
                                                                        releaseType={singleRunOverview.type}
                                                                        maxHeight={290}
                                                                    />
                                                                )}
                                                                {shouldShowRolloutsWidgets && (
                                                                    <WorkflowRunRolloutWidgetsContainer
                                                                        orgIdParam={orgIdParam}
                                                                        appIdParam={appIdParam}
                                                                        releaseIdParam={singleRunOverview.releaseId}
                                                                        phasedGroups={
                                                                            singleRunOverview.phasedReleaseWidgetGroups
                                                                        }
                                                                        appStoreBuildsByDestStepConfigId={
                                                                            singleRunOverview.selectedAppStoreBuildsByDestinationStepId
                                                                        }
                                                                        allDestinations={destinations}
                                                                        selectedDestinationStepConfigIds={
                                                                            selectedDestinationIds
                                                                        }
                                                                        stabIntegrationId={
                                                                            stabilityIntegration?.id ?? null // TODO-WF-OVERVIEW: tweak component logic to support multiple stab integrations (not yet supported)
                                                                        }
                                                                        workflowEnabledSteps={workflowEnabledSteps}
                                                                        archetypeGroupDetailsMap={
                                                                            singleRunOverview.archetypeGroupIdToGroupDetailsMap
                                                                        }
                                                                    />
                                                                )}
                                                            </>
                                                        }
                                                        sideComponent={
                                                            <>
                                                                <WorkflowRunDatesWidgetProps
                                                                    selectedDestinations={destinations.filter((dest) =>
                                                                        selectedDestinationIds.includes(
                                                                            dest.stepConfigId,
                                                                        ),
                                                                    )}
                                                                    workflowRunStatus={singleRunOverview.status}
                                                                    workflowRunTimelinePhase={
                                                                        singleRunOverview.timelinePhase
                                                                    }
                                                                    workflowsEnabledLifecycleSteps={
                                                                        workflowEnabledLifecycleSteps
                                                                    }
                                                                    targetKickoffDate={
                                                                        singleRunOverview.targetKickoffDate
                                                                    }
                                                                    targetSubmitDate={
                                                                        singleRunOverview.targetSubmitDate
                                                                    }
                                                                    targetReleaseDate={
                                                                        singleRunOverview.targetReleaseDate
                                                                    }
                                                                    skippedAt={singleRunOverview.skippedAt}
                                                                    kickedOffAt={singleRunOverview.kickedOffAt}
                                                                    submittedAtByDestinationStepId={
                                                                        singleRunOverview.submittedAtByDestinationStepId
                                                                    }
                                                                    releasedAtByDestinationStepId={
                                                                        singleRunOverview.releasedAtByDestinationStepId
                                                                    }
                                                                    legacyLifecycleAutoRuns={
                                                                        singleRunOverview.lifecycleAutomations
                                                                            ?.legacyLifecycleAutomations
                                                                    }
                                                                    workflowAutoRunsByDestination={
                                                                        singleRunOverview.lifecycleAutomations
                                                                            ?.workflowLifecycleAutomationsByDestination
                                                                    }
                                                                />
                                                                <FeatureCompletionWidget
                                                                    featuresStep={singleRunOverview.featuresStep}
                                                                />
                                                                <Divider
                                                                    style={{
                                                                        margin: '16px 0',
                                                                    }}
                                                                />
                                                                <WorkflowRunStepsGraphWidget
                                                                    runGraph={singleRunOverview.workflowRunGraph}
                                                                    workflowEnabledStepArchetypes={workflowEnabledSteps}
                                                                    archetypeGroupDetailsMap={
                                                                        singleRunOverview.archetypeGroupIdToGroupDetailsMap
                                                                    }
                                                                    stepRunIdToDestinationStepConfigIdsMap={
                                                                        singleRunOverview.stepRunIdToDestinationStepConfigIdsMap
                                                                    }
                                                                    shouldHideNonBlockingArchetypeGroups={
                                                                        shouldHideNonBlockingSteps
                                                                    }
                                                                    nonBlockingStepsConfigMap={
                                                                        nonBlockingStepsConfigMap
                                                                    }
                                                                    allDestinations={destinations}
                                                                    selectedDestinationStepIds={selectedDestinationIds}
                                                                    releaseType={singleRunOverview.type}
                                                                    orgIdParam={orgIdParam}
                                                                    appIdParam={appIdParam}
                                                                    releaseIdParam={singleRunOverview.releaseId}
                                                                />
                                                                <Divider
                                                                    style={{
                                                                        margin: '16px 0',
                                                                    }}
                                                                />
                                                                <ReleasePilotWidget
                                                                    appId={appId}
                                                                    releasePilotId={singleRunOverview.releasePilotId}
                                                                    onReleaseOverview={false}
                                                                />
                                                            </>
                                                        }
                                                        headerBackgroundColor={
                                                            singleRunOverview.overviewDisplayStatus !==
                                                                WorkflowRunOverviewDisplayStatus.Completed &&
                                                            singleRunOverview.overviewDisplayStatus !==
                                                                WorkflowRunOverviewDisplayStatus.Skipped &&
                                                            hexStringToRGBAString(
                                                                colorForWorkflowRunOverviewDisplayStatus(
                                                                    singleRunOverview.overviewDisplayStatus,
                                                                ),
                                                                0.05,
                                                            )
                                                        }
                                                    />
                                                );
                                            })}
                                        </>
                                    )}
                                {!!getWorkflowOverviewIsLoading && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: 4,
                                        }}
                                    >
                                        <Spin />
                                        <Text
                                            style={{
                                                color: runwayColors.primary,
                                            }}
                                        >
                                            Loading overview ...
                                        </Text>
                                    </div>
                                )}
                                {shouldDisplayOverviewFetchFailed && (
                                    <Result
                                        icon={<MehOutlined style={{ color: antGrey.grey5 }} />}
                                        subTitle="Error fetching overview"
                                        extra={
                                            <Button
                                                onClick={() => {
                                                    setShouldDisplayOverviewFetchFailed(false);
                                                    getWorkflowOverview({
                                                        orgId,
                                                        workflowId,
                                                        destinationStepIds: selectedDestinationIds,
                                                    });
                                                }}
                                            >
                                                Try again
                                            </Button>
                                        }
                                    />
                                )}
                            </>
                        :   <>
                                {upcomingReleases?.map((upcomingReleaseSummary) => {
                                    return (
                                        <UpcomingReleaseComponent
                                            key={upcomingReleaseSummary.id}
                                            summary={upcomingReleaseSummary}
                                            orgId={orgId}
                                            appId={appId}
                                            appStoreType={appStoreType}
                                            platform={platform}
                                        />
                                    );
                                })}
                                {nextRelease && (
                                    <NextReleaseComponent
                                        key={nextRelease.id}
                                        summary={nextRelease}
                                        orgId={orgId}
                                        appId={appId}
                                        platform={platform}
                                        appStoreType={appStoreType}
                                        stabilityIntegration={stabilityIntegration}
                                        appStoreIntegration={appStoreIntegration}
                                    />
                                )}
                                {liveRelease && (
                                    <LiveReleaseComponent
                                        key={liveRelease.id}
                                        summary={liveRelease}
                                        orgId={orgId}
                                        appId={appId}
                                        platform={platform}
                                        appStoreType={appStoreType}
                                        stabilityIntegration={stabilityIntegration}
                                        appStoreIntegration={appStoreIntegration}
                                    />
                                )}
                                {pastReleases?.map((pastReleaseSummary) => {
                                    return isReleaseSkipped(pastReleaseSummary.status) ?
                                            <SkippedReleaseComponent
                                                key={pastReleaseSummary.id}
                                                summary={pastReleaseSummary}
                                                orgId={orgId}
                                                appId={appId}
                                            />
                                        :   <PastReleaseComponent
                                                key={pastReleaseSummary.id}
                                                summary={pastReleaseSummary}
                                                orgId={orgId}
                                                appId={appId}
                                                platform={platform}
                                                appStoreType={appStoreType}
                                                stabilityIntegration={stabilityIntegration}
                                                appStoreIntegration={appStoreIntegration}
                                            />;
                                })}
                                {noReleases && (
                                    <Text strong type="secondary">
                                        No releases
                                    </Text>
                                )}
                                {isFetching && <Spin />}
                                {error && (
                                    <Result
                                        icon={<MehOutlined style={{ color: antGrey.grey5 }} />}
                                        subTitle="Error fetching app overview"
                                        extra={<Button onClick={refetch}>Try again</Button>}
                                    />
                                )}
                            </>
                        }
                    </div>
                </Content>
            </Layout>
        </MobileSideBarHeader>
    );
};
